// src/pages/Competition/CompetitionByIdPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getCompetitionById } from "../../services/competitionsServices";
import { getAllUsers } from "../../services/userServices";
import { ToastContainer, toast } from "react-toastify";
import useScrollToTop from "../../hooks/useScrollToTop";
import ScrollIndicator from "./../../hooks/ScrollIndicator";
import { useUser } from "./../../contexts/UserContext";
import { getRoleFromRoleId } from "./../../utils/roleHelper";
import Breadcrumb from "./../../components/Breadcrumb";
import Button from "./../../components/Button/Button";
import { getCompetitionSettingsByCompetitionId } from "../../services/competitionsettingsServices";

// Harmonisation: Font Awesome only
import {
  FaBalanceScale, // Pesées
  FaUsersCog, // Répartitions
  FaStopwatch, // Combats / Tapis
  FaTrophy, // Résultats
  FaChartBar, // Statistiques
  FaGlobe, // Publique
  FaLock, // Privée
  FaCheckCircle, // Active
  FaTimesCircle, // Inactive
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaMedal,
  FaUserNinja,
  FaChevronRight,
} from "react-icons/fa";

let DOMPurify;
try {
  // Optionnel (si installé)
  // eslint-disable-next-line global-require
  DOMPurify = require("dompurify")(window);
} catch {}

const StatChip = ({ icon: Icon, text, className = "" }) => (
  <div
    className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm border backdrop-blur
                border-gray-200/60 dark:border-slate-700/60 bg-white/60 dark:bg-slate-800/60 ${className}`}
  >
    <Icon className="text-base opacity-90" />
    <span>{text}</span>
  </div>
);

const ActionTile = ({ onClick, bg, border, Icon, title }) => (
  <button
    onClick={onClick}
    className={`group cursor-pointer ${bg} ${border} p-6 rounded-2xl shadow-md 
               flex flex-col items-center justify-center transition-all
               hover:scale-[1.02] hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-primary`}
  >
    <Icon className="text-4xl mb-3 opacity-90 group-hover:opacity-100" />
    <h4 className="text-lg font-semibold text-center">{title}</h4>
  </button>
);

const Skeleton = () => (
  <div className="animate-pulse max-w-6xl mx-auto mt-[130px] px-3">
    <div className="h-6 w-40 bg-gray-300/60 dark:bg-slate-700/60 rounded mb-3" />
    <div className="h-10 w-80 bg-gray-300/60 dark:bg-slate-700/60 rounded mb-6" />
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 bg-white/70 dark:bg-slate-900/70 rounded-2xl h-80" />
      <div className="grid grid-cols-2 lg:grid-cols-1 gap-4">
        <div className="h-28 bg-white/70 dark:bg-slate-900/70 rounded-2xl" />
        <div className="h-28 bg-white/70 dark:bg-slate-900/70 rounded-2xl" />
        <div className="h-28 bg-white/70 dark:bg-slate-900/70 rounded-2xl" />
        <div className="h-28 bg-white/70 dark:bg-slate-900/70 rounded-2xl" />
      </div>
    </div>
  </div>
);

const CompetitionByIdPage = () => {
  useScrollToTop();
  const { id } = useParams();
  const { user } = useUser();
  const navigate = useNavigate();
  const userRole = getRoleFromRoleId(user?.role_id);
  const userId = user?.userId;

  const [competition, setCompetition] = useState(null);
  const [organisateur, setOrganisateur] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(false);
  const [competitionSettings, setCompetitionSettings] = useState(null);

  const isOwnerOrAdmin = useMemo(() => {
    if (!competition) return false;
    return (
      userRole === "Administrateur" ||
      competition.organisateur_competition === userId
    );
  }, [competition, userRole, userId]);

  const disciplines = useMemo(() => {
    if (!competition) return [];
    try {
      return Array.isArray(competition.disciplines_competition)
        ? competition.disciplines_competition
        : JSON.parse(competition.disciplines_competition || '["Judo"]');
    } catch {
      return ["Judo"];
    }
  }, [competition]);

  const safeHorairesHtml = useMemo(() => {
    const raw = competition?.horaires_pesees || "";
    if (DOMPurify) return DOMPurify.sanitize(raw);
    return raw; // fallback (contenu interne)
  }, [competition?.horaires_pesees]);

  const fetchCompetition = async () => {
    try {
      const data = await getCompetitionById(id);
      setCompetition(data);
      setLoading(false);
    } catch {
      toast.error("Erreur lors du chargement de la compétition.");
      navigate("/");
    }
  };

  const fetchOrganisateur = async (organisateurId) => {
    try {
      const users = await getAllUsers();
      const u = users.find((x) => x.id === organisateurId);
      setOrganisateur(u || null);
    } catch {
      toast.error("Erreur lors du chargement de l'organisateur.");
    }
  };

  useEffect(() => {
    const fetchSettings = async () => {
      if (!competition?.id) return;
      try {
        const data = await getCompetitionSettingsByCompetitionId(
          competition.id
        );
        if (Array.isArray(data) && data.length > 0) {
          setCompetitionSettings(data[0]);
        }
      } catch (e) {
        console.error("Erreur chargement des settings compétition:", e);
      }
    };

    fetchSettings();
  }, [competition]);

  useEffect(() => {
    fetchCompetition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (competition) {
      if (!isOwnerOrAdmin) {
        toast.error("Vous n'avez pas l'autorisation d'accéder à cette page.");
        navigate("/");
        return;
      }
      fetchOrganisateur(competition.organisateur_competition);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competition]);

  if (loading) return <Skeleton />;

  if (!competition || !organisateur) {
    return (
      <div className="max-w-4xl mx-auto mt-[140px] px-3">
        <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 p-4 text-red-700 dark:text-red-300">
          Erreur lors du chargement des données.
        </div>
      </div>
    );
  }

  

  const cover = competition.photo_URL || "/img/competition_default.jpg";

  const ActionCard = ({ onClick, Icon, title, subtitle, color = "blue" }) => {
    const palette = {
      blue: {
        ring: "ring-blue-400/30",
        grad: "from-blue-500/15 to-blue-500/0",
        icon: "text-blue-600 dark:text-blue-300",
        hoverShadow: "hover:shadow-blue-500/25",
      },
      green: {
        ring: "ring-green-400/30",
        grad: "from-green-500/15 to-green-500/0",
        icon: "text-green-600 dark:text-green-300",
        hoverShadow: "hover:shadow-green-500/25",
      },
      yellow: {
        ring: "ring-yellow-400/30",
        grad: "from-yellow-500/15 to-yellow-500/0",
        icon: "text-yellow-600 dark:text-yellow-300",
        hoverShadow: "hover:shadow-yellow-500/25",
      },
      red: {
        ring: "ring-red-400/30",
        grad: "from-red-500/15 to-red-500/0",
        icon: "text-red-600 dark:text-red-300",
        hoverShadow: "hover:shadow-red-500/25",
      },
      purple: {
        ring: "ring-purple-400/30",
        grad: "from-purple-500/15 to-purple-500/0",
        icon: "text-purple-600 dark:text-purple-300",
        hoverShadow: "hover:shadow-purple-500/25",
      },
    }[color];

    return (
      <button
        onClick={onClick}
        className={`group relative overflow-hidden rounded-2xl border border-gray-200/70 dark:border-slate-700/70 
                  bg-white/70 dark:bg-slate-900/60 backdrop-blur p-5 text-left shadow-md 
                  ring-1 ${palette.ring} transition-all duration-300 
                  hover:-translate-y-0.5 ${palette.hoverShadow}`}
      >
        {/* Shine */}
        <span
          className="pointer-events-none absolute inset-0 -translate-x-[150%] 
                   bg-gradient-to-r from-transparent via-white/30 to-transparent 
                   opacity-0 transition-all duration-700 ease-out
                   group-hover:translate-x-[150%] group-hover:opacity-100"
        />

        {/* Accent blob */}
        <span
          className={`pointer-events-none absolute -right-6 -top-6 h-16 w-16 rounded-full 
                    bg-gradient-to-br ${palette.grad} blur-md`}
        />

        <div className="flex items-center gap-4">
          {/* Icône circulaire */}
          <div
            className={`flex h-12 w-12 items-center justify-center rounded-xl 
                      bg-gradient-to-br ${palette.grad} ${palette.icon}
                      transition-transform duration-300 group-hover:scale-110`}
          >
            <Icon className="text-2xl" />
          </div>

          {/* Titre + sous-titre */}
          <div className="min-w-0">
            <h4 className="text-base font-semibold text-gray-900 dark:text-white">
              {title}
            </h4>
            {subtitle && (
              <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
                {subtitle}
              </p>
            )}
          </div>

          {/* Chevron animé */}
          <FaChevronRight
            className="ml-auto text-gray-400 transition-all duration-300 
                     opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0"
          />
        </div>
      </button>
    );
  };

  return (
    <div className="flex flex-col w-full mx-auto px-3 mt-[130px] sm:mt-[130px] md:mt-[150px] lg:mt-[150px] xl:mt-[150px] dark:text-white max-w-[1280px]">
      <ScrollIndicator />

      {/* Header */}
      <div className="text-center space-y-2 mb-6">
        <h2 className="text-sm uppercase tracking-widest text-gray-500 dark:text-gray-400">
          Organisateur
        </h2>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900 dark:text-white">
          {competition.nom_competition}
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {organisateur?.nom} {organisateur?.prenom}
        </p>
      </div>

      <Breadcrumb competitionName={competition.nom_competition} />

      {/* Layout principal */}
      <div className="flex flex-col lg:flex-row gap-6 max-w-6xl mx-auto mb-10 mt-4">
        {/* Carte principale */}
        <div className="bg-white/80 dark:bg-slate-900/80 rounded-2xl overflow-hidden border border-gray-200 dark:border-slate-700 shadow-lg lg:flex-1">
          <div className="relative">
            <img
              src={cover}
              alt={competition.nom_competition}
              className="w-full h-64 object-cover cursor-pointer"
              onClick={() => setLightbox(true)}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" />
            {/* Badges overlay */}
            <div className="absolute bottom-3 left-3 flex flex-wrap gap-2">
              <StatChip
                icon={competition.is_active ? FaCheckCircle : FaTimesCircle}
                text={competition.is_active ? "Active" : "Inactive"}
                className={
                  competition.is_active ? "text-green-700" : "text-red-700"
                }
              />
              <StatChip
                icon={competition.is_public ? FaGlobe : FaLock}
                text={competition.is_public ? "Publique" : "Privée"}
                className="text-white bg-black/40 border-white/20"
              />
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Infos principales */}
            <div className="grid sm:grid-cols-2 gap-4 text-sm dark:text-gray-300">
              {/* Lieu */}
              <div className="flex items-start gap-3">
                <FaMapMarkerAlt className="mt-0.5 text-primary opacity-90 shrink-0" />
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    Lieu
                  </p>
                  <p>{competition.lieu_competition}</p>
                </div>
              </div>

              {/* Date */}
              <div className="flex items-start gap-3">
                <FaCalendarAlt className="mt-0.5 text-primary opacity-90 shrink-0" />
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    Date
                  </p>
                  <p>
                    {new Date(competition.date_competition).toLocaleDateString(
                      "fr-FR"
                    )}
                  </p>
                </div>
              </div>

              {/* Niveau */}
              <div className="flex items-start gap-3">
                <FaMedal className="mt-0.5 text-primary opacity-90 shrink-0" />
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    Niveau
                  </p>
                  <p>{competition.level_competition}</p>
                </div>
              </div>

              {/* Disciplines */}
              <div className="flex items-start gap-3">
                <FaUserNinja className="mt-0.5 text-primary opacity-90 shrink-0" />
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900 dark:text-white">
                    Disciplines
                  </p>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {disciplines.map((d) => (
                      <span
                        key={d}
                        className="text-xs px-2 py-1 rounded-full border border-gray-300 dark:border-slate-700
                     bg-white/60 dark:bg-slate-800/60 backdrop-blur"
                      >
                        {d}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Quota max compétiteurs */}
              {competitionSettings?.quota_competition != null &&
                Number(competitionSettings.quota_competition) > 0 && (
                  <div className="flex items-start gap-3 sm:col-span-2">
                    <FaUsersCog className="mt-0.5 text-primary opacity-90 shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white">
                        Quota maximum de compétiteurs
                      </p>
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        {competitionSettings.quota_competition} compétiteur
                        {competitionSettings.quota_competition > 1
                          ? "s"
                          : ""}{" "}
                        maximum inscrits.
                        <br />
                        Au-delà de ce quota, les judokas seront automatiquement
                        considérés comme{" "}
                        <span className="font-semibold">
                          sur liste d&apos;attente
                        </span>
                        .
                      </p>
                    </div>
                  </div>
                )}
            </div>

            {/* Horaires des pesées (rich text) */}
            {competition.horaires_pesees && (
              <div className="rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-50/60 dark:bg-slate-800/60 p-4">
                <h3 className="text-base font-semibold mb-2 flex items-center gap-2">
                  <FaBalanceScale className="opacity-80" />
                  Horaires des pesées
                </h3>
                <div
                  className="prose prose-sm dark:prose-invert max-w-none prose-headings:mt-3 prose-p:my-2"
                  dangerouslySetInnerHTML={{ __html: safeHorairesHtml }}
                />
              </div>
            )}

            {/* Barre d’actions (ex: retour) */}
            <div className="flex flex-wrap gap-2 pt-2">
              <Button variant="outline" onClick={() => navigate(-1)}>
                Retour
              </Button>
            </div>
          </div>
        </div>

        {/* Tuiles d’actions — version animée */}
        <div className="grid grid-cols-2 lg:grid-cols-1 gap-4 w-full lg:w-1/3">
          <ActionCard
            onClick={() => navigate(`/competition/${id}/judokas`)}
            Icon={FaBalanceScale}
            title="Pesées"
            subtitle="Présences, contrôle des poids & catégories"
            color="blue"
          />
          <ActionCard
            onClick={() => navigate(`/competition/${id}/repartition`)}
            Icon={FaUsersCog}
            title="Répartitions"
            subtitle="dans des distributions (poules/tableaux)"
            color="green"
          />
          <ActionCard
            onClick={() => navigate(`/competition/${id}/tapis`)}
            Icon={FaStopwatch}
            title="Combats sur les Tapis"
            subtitle="Répartition des distributions sur les tapis et suivi en direct"
            color="yellow"
          />
          <ActionCard
            onClick={() => navigate(`/competition/${id}/resultats`)}
            Icon={FaTrophy}
            title="Résultats"
            subtitle="Classements et podiums"
            color="red"
          />
          <ActionCard
            onClick={() => navigate(`/competition/${id}/stats`)}
            Icon={FaChartBar}
            title="Statistiques"
            subtitle="Indicateurs clés et analytics"
            color="purple"
          />
        </div>
      </div>

      {/* Lightbox image */}
      {lightbox && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setLightbox(false)}
        >
          <img
            src={cover}
            alt="Agrandissement"
            className="max-w-5xl max-h-[85vh] rounded-xl shadow-2xl"
          />
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default CompetitionByIdPage;
